"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { signOutAction } from "@/app/actions";
import {
  AccountTabMobileHeader,
  accountTabHeaderPaddingTop,
} from "@/components/account/account-tab-mobile-header";
import { buildAccountMenuSections } from "@/components/account/account-menu-sections";
import { AccountMenuView } from "@/components/account/account-menu-view";
import { AccountProfileCard } from "@/components/account/account-profile-card";
import type { PaymentStatus } from "@/components/settings/payment-status-pill";
import { appendSecurityLogFormData } from "@/lib/security-log-client";
import { Z } from "@/lib/overlay/z-scale";

type ProfileTabUser = {
  id: string;
  email?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  user_metadata?: {
    username?: string | null;
    avatar_url?: string | null;
  } | null;
};

interface ProfileTabSliderProps {
  open: boolean;
  onOpenChangeAction: (open: boolean) => void;
  user: ProfileTabUser;
  myProfileHref: string;
  offerActionCount?: number;
  paymentStatus: PaymentStatus;
}

export function ProfileTabSlider({
  open,
  onOpenChangeAction,
  user,
  myProfileHref,
  offerActionCount = 0,
  paymentStatus,
}: ProfileTabSliderProps) {
  const tNav = useTranslations("nav");
  const tSettings = useTranslations("settings");
  const tCommon = useTranslations("common");
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [lastPathname, setLastPathname] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const profileUsername =
    typeof user.username === "string" && user.username.trim()
      ? user.username.trim()
      : typeof user.user_metadata?.username === "string" && user.user_metadata.username.trim()
        ? user.user_metadata.username.trim()
        : null;

  const profileLabel = profileUsername
    ? `@${profileUsername}`
    : user.email?.split("@")[0] ?? tNav("accountTitle");

  const profileSub = profileUsername ? user.email ?? undefined : undefined;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setLastPathname(pathname);
      return;
    }
    if (lastPathname && lastPathname !== pathname) {
      onOpenChangeAction(false);
    }
    setLastPathname(pathname);
  }, [open, onOpenChangeAction, pathname, lastPathname]);

  const handleClose = () => {
    onOpenChangeAction(false);
  };

  const handleNavigate = () => {
    onOpenChangeAction(false);
  };

  const handleSignOut = useCallback(async () => {
    const formData = new FormData();
    appendSecurityLogFormData(formData);
    await signOutAction(formData);
    onOpenChangeAction(false);
  }, [onOpenChangeAction]);

  const sections = buildAccountMenuSections({
    tNav,
    tSettings,
    tCommon,
    myProfileHref,
    offerActionCount,
    paymentStatus,
    onSignOut: () => void handleSignOut(),
  });

  const content = (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          className="fixed top-0 right-0 flex h-dvh w-full flex-col bg-background md:hidden"
          style={{ zIndex: Z.overlayPanel }}
        >
          <AccountTabMobileHeader
            title={tNav("accountTitle")}
            onBackAction={handleClose}
            backAriaLabel={tCommon("back")}
            measureHeight={open}
          />

          <div
            ref={scrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            style={{
              paddingTop: accountTabHeaderPaddingTop(),
              WebkitOverflowScrolling: "touch",
            }}
          >
            <AccountProfileCard
              userId={user.id}
              email={user.email}
              username={profileUsername}
              avatarUrl={user.avatarUrl}
              myProfileHref={myProfileHref}
              profileLabel={profileLabel}
              profileSub={profileSub}
              onNavigate={handleNavigate}
              className="mb-5"
            />

            <AccountMenuView sections={sections} onNavigate={handleNavigate} />
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
